import React from 'react';
import { Box, Stack, Typography } from '@mui/material';
import type { TypographyProps } from '@mui/material/Typography';
import { MOBILE_DENSITY } from '../../theme/mobileDensity';

export interface PageHeaderProps {
  /** Main heading content. */
  title: React.ReactNode;
  /** Optional supporting sentence rendered beneath the title. */
  subtitle?: React.ReactNode;
  /**
   * Optional second supporting line rendered beneath `subtitle`, in a smaller,
   * lighter style. Use for a secondary/less-prominent message (e.g. a welcome
   * blurb below a primary call-to-action subtitle).
   */
  secondarySubtitle?: React.ReactNode;
  /** Optional small overline rendered above the title. */
  eyebrow?: React.ReactNode;
  /** Optional right-aligned slot (e.g. a primary action button). */
  actions?: React.ReactNode;
  /** Typography variant applied to the title. Defaults to `h1`. */
  titleVariant?: TypographyProps['variant'];
}

/**
 * Top-of-page heading block: eyebrow → title → subtitle, with an
 * optional right-aligned actions slot.
 *
 * Layout references StateSpin's page headers (spec §3.1). Visual
 * density is driven entirely by the theme's typography tokens so
 * consumers rarely need to override styles.
 */
export const PageHeader: React.FC<PageHeaderProps> = ({
  title,
  subtitle,
  secondarySubtitle,
  eyebrow,
  actions,
  titleVariant = 'h1',
}) => {
  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: { xs: 1.5, sm: MOBILE_DENSITY.sectionGap },
        width: '100%',
        flexWrap: { xs: 'wrap', sm: 'nowrap' },
      }}
    >
      <Stack spacing={0.5} sx={{ flex: 1, minWidth: 0, flexBasis: { xs: '100%', sm: 0 } }}>
        {eyebrow && (
          <Typography
            variant="overline"
            component="div"
            color="text.secondary"
          >
            {eyebrow}
          </Typography>
        )}
        <Typography
          variant={titleVariant}
          component="h1"
          sx={{ overflowWrap: 'anywhere' }}
        >
          {title}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary">
            {subtitle}
          </Typography>
        )}
        {secondarySubtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ display: 'block' }}>
            {secondarySubtitle}
          </Typography>
        )}
      </Stack>
      {actions && (
        <Box sx={{
          flexShrink: 1,
          display: 'flex',
          flexWrap: 'wrap',
          maxWidth: '100%',
          width: { xs: '100%', sm: 'auto' },
          gap: 1,
        }}>{actions}</Box>
      )}
    </Box>
  );
};

export default PageHeader;
